import { AxiosResponse } from 'axios';

import {
  CarePincodeDataResponse,
  HealthPolicyStatus,
  HealthPolicyStatusResponse
} from '@store/health-status/types';

import { client } from '../client';

import { healthEndPoints } from './endpoints';
import {
  ComparePremiumChangePayload,
  ComparePremiumChangeResponse,
  VerifyPrintTokenResponse
} from './types';

export const healthProposalAPIs = {
  getHealthProposal: (
    policyId: string,
    fromReview?: boolean
  ): Promise<AxiosResponse<HealthPolicyStatusResponse>> => {
    return client
      .get<HealthPolicyStatusResponse>(healthEndPoints.proposal(policyId), {
        from_review: fromReview
      })
      .then((response) => {
        // KYC Done or FORM WIP means that the form is ready to be filled and the location should not be replaced.
        if (
          ![HealthPolicyStatus.FORM_WIP, HealthPolicyStatus.KYC_DONE].includes(
            response?.data?.status as HealthPolicyStatus
          )
        ) {
          window.location.replace(healthEndPoints.status(policyId));
        }
        return response;
      });
  },
  healthFinalSubmit: async (
    policyId: string,
    data?: Record<string, any>
  ): Promise<AxiosResponse<any>> => {
    const params = data || {};
    return client.post(`${healthEndPoints.proposal(policyId)}/submit`, params);
  },
  healthAdaptiveProposal: (
    policyId: string,
    params: any
  ): Promise<AxiosResponse<any>> => {
    return client.post(healthEndPoints.proposal(policyId), {
      policyId,
      proposal_form: params
    });
  },
  healthAdaptiveProposalSubmit: async (
    policyId: string,
    paperless?: boolean,
    confirm_submit?: boolean,
    app_no?: string
  ): Promise<AxiosResponse<any>> => {
    return client.post(healthEndPoints.proposalSubmit(policyId), {
      only_soft_copy: paperless,
      // accept change in premium
      confirm_submit,
      ...(app_no && { app_no })
    });
  },
  getAdityaBirlaPincodeData: (
    pincode: string,
    product: string
  ): Promise<AxiosResponse<any>> => {
    return client.get(healthEndPoints.adityaBirlaPincodeData(pincode, product));
  },
  getNivaBupaBankNames: (ifscCode: string): Promise<AxiosResponse<any>> => {
    return client.get(healthEndPoints.nivaBupaBankNames(ifscCode));
  },
  getERGOBankNames: (
    bankName: string,
    product: string
  ): Promise<AxiosResponse<any>> => {
    return client.get(healthEndPoints.ergoBankNames(bankName, product));
  },
  getERGOBankNamesByIFSC: (
    ifscCode: string,
    product: string
  ): Promise<AxiosResponse<any[]>> => {
    return client.get(healthEndPoints.ergoBankNamesByIFSC(ifscCode, product));
  },
  getERGOEnergySilverArea: (pincode: string): Promise<AxiosResponse<any>> => {
    return client.get(healthEndPoints.ergoEnergySilverArea(pincode));
  },
  getERGOCityState: (
    pincode: string,
    product: string
  ): Promise<AxiosResponse<any>> => {
    return client.get(healthEndPoints.ergoCityState(pincode, product));
  },
  getERGOOptimaCityState: (
    pincode: string,
    product: string
  ): Promise<AxiosResponse<any>> => {
    return client.get(healthEndPoints.ergoOptimaCityState(pincode, product));
  },
  getCarePincodeData: (
    pincode: string,
    product: string
  ): Promise<AxiosResponse<CarePincodeDataResponse>> => {
    return client.get(healthEndPoints.carePincodeData(pincode, product));
  },
  comparePremiumChange: (
    policyId: string,
    payload: ComparePremiumChangePayload
  ): Promise<
    AxiosResponse<ComparePremiumChangeResponse, ComparePremiumChangePayload>
  > => {
    return client.post(healthEndPoints.comparePremiumChange(policyId), {
      ...payload
    });
  },
  verifyPrintToken: (
    token: string
  ): Promise<AxiosResponse<VerifyPrintTokenResponse>> => {
    return client.post(healthEndPoints.verifySummaryToken(), { token });
  }
};
